/**
 * DFE Mutations + materials query
 */

import { createDfe, getDfeMaterials } from '@/domains/jobs/api/dfeApi';
import { queryKeys } from '@/lib/query-client';
import type { CreateDfeBody } from '@skewb-works/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Fetch the DFE materials catalogue. This is a global lookup (not per-job) so
 * it uses a dedicated query key outside of `queryKeys.jobs.*`.
 */
export function useDfeMaterials() {
  return useQuery({
    queryKey: ['dfe-materials'] as const,
    queryFn: () => getDfeMaterials(),
    staleTime: 10 * 60 * 1000, // rates change rarely — cache for 10 minutes
  });
}

export function useCreateDfe(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDfeBody) => createDfe(jobId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.dfe(String(jobId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.activityLog(String(jobId)) });
    },
    meta: {
      successTitle: 'DFE Created',
      successMessage: 'DFE record created successfully',
    },
  });
}
